import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { CheckCircle2, Sparkles, ShieldCheck, Clock, TrendingDown } from "lucide-react";
import { usePublicConfig } from "@/lib/usePublicConfig";
import WhyCheckExpert from "@/components/WhyCheckExpert";

function fmt(cents) {
  return `€${(cents / 100).toFixed(2).replace(".", ",")}`;
}

export default function Prijzen() {
  const cfg = usePublicConfig();
  const single = cfg?.pricing?.single_cents || 499;
  const FALLBACK_PACKS = [
    { id: "pack-3", label: "3-pack", credits: 3, price_cents: 999, validity_months: 12, visible: true, highlight: false, sort_order: 10 },
    { id: "pack-20", label: "20-pack — voordeelpakket", credits: 20, price_cents: 1995, validity_months: 12, visible: true, highlight: true, sort_order: 20 },
    { id: "pack-50", label: "50-pack — voordeelpakket", credits: 50, price_cents: 2995, validity_months: 12, visible: true, highlight: false, sort_order: 30 },
  ];
  const rawPacks = cfg?.credit_packs?.length ? cfg.credit_packs : FALLBACK_PACKS;
  const packs = rawPacks.filter((p) => p.visible !== false);
  // Eerste pack = "standaard" bundle, rest = extra trigger
  const main = packs.find((p) => p.id === "pack-3") || packs[0];
  const extras = packs.filter((p) => p.id !== (main?.id || "pack-3"));

  return (
    <div className="py-16 font-modern" data-testid="pricing-page">
      <Seo path="/prijzen" />
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#002FA7]">Eerlijk geprijsd</span>
          <h1 className="font-editorial text-5xl sm:text-6xl font-medium text-ink mt-3" style={{ letterSpacing: "-0.025em" }}>
            Eén heldere prijs. Geen verborgen kosten.
          </h1>
          <p className="text-ink-soft text-lg mt-5">
            Betaal alleen wanneer je een document laat checken. Geen abonnement, geen automatische verlenging.
          </p>
        </div>

        {/* Hoofdaanbod: 1 check + 3-pack */}
        <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Single */}
          <div className="rounded-2xl border border-border bg-white p-8" data-testid="pricing-single">
            <div className="text-sm font-semibold text-ink-soft">Per analyse</div>
            <div className="font-editorial text-5xl font-medium text-ink mt-2" style={{ letterSpacing: "-0.025em" }}>{fmt(single)}</div>
            <p className="text-sm text-ink-soft mt-1">Eenmalig — BTW inbegrepen</p>
            <ul className="mt-8 space-y-3 text-sm text-ink">
              {["1 volledig rapport","Score 0–100 + actiestappen","Wettelijke verwijzingen BE/NL","30 dagen toegang","PDF-export"].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#10B981] mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/checks" className="mt-8 inline-flex items-center justify-center w-full px-6 py-3.5 rounded-full bg-ink hover:bg-ink-soft text-white font-semibold transition-colors" data-testid="pricing-single-cta">
              Kies een check
            </Link>
          </div>

          {/* 3-pack */}
          {main && (
            <PackCard pack={main} single={single} highlight testid="pricing-bundle" />
          )}
        </div>

        {/* Extra trigger: jaarpakketten */}
        {extras.length > 0 && (
          <div className="mt-20" data-testid="pricing-extras">
            <div className="text-center max-w-3xl mx-auto">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#10B981]">Voordeelpakketten</span>
              <h2 className="font-editorial text-4xl sm:text-5xl font-medium text-ink mt-3" style={{ letterSpacing: "-0.025em" }}>
                Meer documenten? Bespaar fors.
              </h2>
              <p className="text-ink-soft text-base mt-4 max-w-2xl mx-auto">
                Krijg meerdere checks in één keer en bespaar tot 70%. Credits zijn één jaar geldig, geen automatische verlenging. Veertien dagen voor het verloop sturen we je een herinnering.
              </p>
            </div>
            <div className="mt-10 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {extras.map((p) => (
                <PackCard key={p.id} pack={p} single={single} testid={`pricing-pack-${p.id}`} />
              ))}
            </div>
          </div>
        )}

        {/* Trust strip */}
        <div className="mt-16 grid sm:grid-cols-3 gap-4" data-testid="prijzen-trust">
          {[
            { Icon: Sparkles, title: "Direct beschikbaar", text: "Rapport in &lt; 60 seconden. Geen wachtrij, geen afspraak." },
            { Icon: ShieldCheck, title: "GDPR-conform", text: "Documenten versleuteld geüpload en automatisch verwijderd na 30 dagen." },
            { Icon: Clock, title: "Geen abonnement", text: "Eenmalig betalen. Geen automatische verlenging, geen verborgen kosten." },
          ].map(({ Icon, title, text }) => (
            <div key={title} className="rounded-2xl bg-white border border-border p-5">
              <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center text-[#002FA7]">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-editorial text-sm font-semibold text-ink mt-3">{title}</h3>
              <p className="text-xs text-ink-soft mt-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: text }} />
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-ink-subtle mt-10 max-w-2xl mx-auto">
          Beveiligd betalen via MultiSafepay · 256-bit encryptie · Bancontact · iDEAL · Wero · Visa · Mastercard · Apple Pay · Google Pay
        </p>
        <p className="text-center text-[11px] text-ink-subtle mt-3 max-w-2xl mx-auto">
          CheckExpert is een digitale dienst die onmiddellijk wordt uitgevoerd. Conform art. VI.53 WER doet u door uw aankoop afstand van uw herroepingsrecht.
        </p>
      </div>

      <WhyCheckExpert ctaHref="/checks" ctaLabel="Start nu — vanaf €4,99" />
    </div>
  );
}

function PackCard({ pack, single, highlight = false, testid }) {
  const perCheck = Math.round(pack.price_cents / pack.credits);
  const savingsPercent = Math.round((1 - perCheck / single) * 100);
  const savingsTotal = Math.max(0, single * pack.credits - pack.price_cents);
  const validity = pack.validity_months || 12;
  const cardCls = highlight || pack.highlight
    ? "rounded-2xl border-2 border-[#002FA7] bg-white p-8 relative"
    : "rounded-2xl border border-border bg-white p-8 relative hover:border-[#002FA7]/40 transition-colors";

  return (
    <div className={cardCls} data-testid={testid}>
      {(highlight || pack.highlight) && (
        <span className="absolute -top-3 left-8 bg-[#002FA7] text-white text-xs uppercase tracking-wider font-bold px-3 py-1 rounded-full">
          {pack.highlight ? "Aanbevolen" : "Populair"} · bespaar {savingsPercent}%
        </span>
      )}
      <div className="text-sm font-semibold text-[#002FA7]">{pack.label}</div>
      <div className="font-editorial text-5xl font-medium text-ink mt-2" style={{ letterSpacing: "-0.025em" }}>{fmt(pack.price_cents)}</div>
      <p className="text-sm text-ink-soft mt-1">
        {pack.credits} {pack.credits === 1 ? "check" : "checks"} · slechts <b className="text-ink">{fmt(perCheck)}</b> per check
      </p>
      {savingsTotal > 0 && (
        <p className="text-xs text-[#10B981] font-semibold mt-1">
          Je bespaart {fmt(savingsTotal)} t.o.v. losse checks
        </p>
      )}
      <ul className="mt-7 space-y-3 text-sm text-ink">
        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#10B981] mt-0.5" /><span>{pack.credits} analyses, vrije keuze checkers</span></li>
        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#10B981] mt-0.5" /><span>Credits {validity} {validity === 1 ? "maand" : "maanden"} geldig</span></li>
        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#10B981] mt-0.5" /><span>Geen automatische verlenging — herinnering 14 dagen voor verloop</span></li>
        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#10B981] mt-0.5" /><span>Ideaal voor wie meerdere documenten laat nakijken</span></li>
        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#10B981] mt-0.5" /><span>Prioriteit-support</span></li>
      </ul>
      <Link to={`/credits?pack=${pack.id}`} className="mt-7 inline-flex items-center justify-center w-full px-6 py-3.5 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white font-semibold transition-colors" data-testid={`${testid}-cta`}>
        Koop {pack.label.split(" — ")[0]}
      </Link>
    </div>
  );
}
